import { Injectable } from '@angular/core';
import {
  Firestore,
  Timestamp,
  doc,
  getDoc,
  setDoc,
} from '@angular/fire/firestore';
import { Observable, from, map } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import {
  FuelEntries,
  FuelEntriesFirebase,
  FuelEntry,
  FuelEntryFirebase,
} from '../models/fuel.model';
import { toDate, toTimestamp } from 'src/app/shared/utils/firestore-timestamp-utils';

@Injectable({
  providedIn: 'root',
})
export class FuelService {
  constructor(private firestore: Firestore) {}

  getFuelData(vanId: string): Observable<FuelEntries | null> {
    const docRef = doc(this.firestore, `vans/${vanId}/fuelData`, 'data');
    return from(getDoc(docRef)).pipe(
      map((doc) => {
        if (doc.exists()) {
          const firebaseData = doc.data() as FuelEntriesFirebase;
          return this.transformFromFirebase(firebaseData);
        }
        return null;
      })
    );
  }

  upsertFuelData(vanId: string, data: FuelEntries): Observable<void> {
    const docRef = doc(this.firestore, `vans/${vanId}/fuelData`, 'data');
    const firebaseData = this.transformToFirebase(data);

    // Always use setDoc to write the entire document
    return from(setDoc(docRef, firebaseData));
  }

  private transformFromFirebase(
    firebaseData: FuelEntriesFirebase
  ): FuelEntries {
    const entries: FuelEntry[] = firebaseData.entries.map(
      (firebaseEntry: FuelEntryFirebase) => {
        const { date, ...restOfFirebaseEntry } = firebaseEntry;
        return {
          ...restOfFirebaseEntry,
          id: uuidv4(),
          date: toDate(date),
        };
      }
    );
    return { entries };
  }

  private transformToFirebase(data: FuelEntries): FuelEntriesFirebase {
    const entries: FuelEntryFirebase[] = data.entries.map((fuelEntryWithId) => {
      const { id, date, ...restOfFuelEntry } = fuelEntryWithId;
      return {
        ...restOfFuelEntry,
        date: toTimestamp(date) as Timestamp,
      };
    });
    return { entries };
  }
}
